// components/pulse/PulsePage.tsx

import React, { useState } from 'react';
import PulseNav from './PulseNav';
import PulseContent, { ViewRenderPayload } from './PulseContent';
import { PivotTableData} from '../../models/PulseData';
import { samplePivotData} from '../../data/PulseContent.samples';
import './PulsePage.css';

import logger from '../../utils/logger';
import { useLocation, Navigate} from 'react-router-dom';
import ServicesGroupSelector from './ServicesGroupSelector';
import { useCustomer } from '../../contexts/CustomerContext';
import { TimeSliceViewType } from '@/models/TimeSlice';
import { useDateRangeManager } from '../../hooks/useDateRangeManager';
import { usePulsePivotData } from '../../hooks/usePulsePivotData';
import { usePulseServiceAttributePivotData } from '../../hooks/usePulseServiceAttributePivotData';
import { usePulseServiceAttributesData } from '../../hooks/usePulseServiceAttributesData';
import { usePulseGroupAttributeSummaryData } from '../../hooks/usePulseGroupAttributeSummaryData';
import ErrorDisplay from '../common/ErrorDisplay';
import { SummaryType, NotifierType } from './PulseNav'; // Ensure SummaryType & NotifierType are exported
  
const PulsePage: React.FC = () => {
  const [activeSummary, setActiveSummary] = useState<SummaryType>('pivot');

  // New state for attribute summary for a services group
  const [selectedGroupAttribute, setSelectedGroupAttribute] = useState<string | null>(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedServicesGroupId = queryParams.get('view');

  const { selectedCustomer, availableCustomers, error: customerError } = useCustomer();

  const { 
    pulseView,
    timeSliceWindow,
    handlePulseViewChange,
    handleEndDateChange, 
    minSelectableDateString, 
    maxSelectableDateString 
  } = useDateRangeManager({ selectedCustomer });

  const pivotTableState = usePulsePivotData( {
    selectedCustomer,
    selectedServicesGroupId,
    timeSliceWindow
  });

  // Store the currently selected service name if activeSummary is for a service
  const [selectedServiceForAttribute, setSelectedServiceForAttribute] = useState<string | null>(null);

  // Store the attribute selected for pivot view within the selectedServiceForAttribute (default is 'msg')
  const [selectedAttributeForServicePivot, setSelectedAttributeForServicePivot] = useState<string | null>(null);

  const serviceAttributePivotState 
    = usePulseServiceAttributePivotData({
      selectedCustomer,
      selectedServicesGroupId,
      serviceName: selectedServiceForAttribute,
      attributeName: selectedAttributeForServicePivot,
      timeSliceWindow,
    });

  const { serviceAttributesData, isLoadingAttributes, attributeErrors } 
    = usePulseServiceAttributesData({
      selectedCustomer,
      selectedServicesGroupId,
      selectedServiceForAttribute,
      keyAttributes: selectedCustomer?.keyAttributes || [],
      timeSliceWindow,
    });

  const groupAttributeSummaryData
    = usePulseGroupAttributeSummaryData({
      selectedCustomer,
      selectedServicesGroupId,
      selectedGroupAttribute,
      timeSliceWindow,
    });

  const capitalizeFirstLetter = (string: string | null): string => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleSelectSummary = (summary: SummaryType) => {

    setActiveSummary(summary);
    // setSelectedAttributeForServicePivot(null)

    if (summary.startsWith('service_')) {

      const serviceName = summary.substring('service_'.length);
      setSelectedServiceForAttribute(serviceName);
      setSelectedAttributeForServicePivot(selectedCustomer?.servicePivotAttribute || '');
      // Fetch attribute data when a service is selected (see useEffect below)

    } else if (summary.startsWith('attribute_')) {

      const attributeName = summary.substring('attribute_'.length);
      logger.debug(`Handling attribute selection for group: ${attributeName}`);
      setSelectedGroupAttribute(attributeName);
      setSelectedServiceForAttribute(null); // Clear service selection if group attribute is chosen
      setSelectedAttributeForServicePivot(null); // Clear pivot selection if group attribute is chosen
      
    } else {

      // Clear if not a service summary
      setSelectedServiceForAttribute(null);
      setSelectedAttributeForServicePivot(null);

      // reset state group attribute data
      setSelectedGroupAttribute(null);
    }    
  };

  const servicesGroupForDisplay = capitalizeFirstLetter( selectedServicesGroupId);

  if (customerError) {
    return (
      <div className="pulse-page-container error-message">
        <p>Error loading customer data: {customerError}. <a href="/teams">Select another team?</a></p>
      </div>);
  }

  // If no customer is selected (e.g., user landed on /pulse directly without selection), redirect to /teams
  if (!selectedCustomer) {
    logger.warn('No customer selected for /pulse, redirecting to /teams.');
    return <Navigate to="/teams" replace />;
  }  

  // If no view segment is selected, show the selector page
  if ( selectedCustomer && !selectedServicesGroupId) {
    const customerSegments = selectedCustomer.servicesGroup;
    if (customerSegments && customerSegments.length === 1) {
      // If there's only one segment, navigate directly to it
      const singleSegmentId = customerSegments[0].id;
      const navigateToUrl = `${location.pathname}?view=${encodeURIComponent(singleSegmentId)}`;
      logger.info(`Single service group found ('${singleSegmentId}'), navigating to: ${navigateToUrl}`);
      return <Navigate to={navigateToUrl} replace />;
    } else {
      // Otherwise, show the selector
      return (
        <ServicesGroupSelector
          customerName={selectedCustomer.name}
          segments={customerSegments}
          currentUrl={location.pathname} // Pass current base path (e.g., /pulse)
        />
      );
    }
  }

  if (pivotTableState.isLoading && activeSummary === 'pivot') {
    return <div className="pulse-page-container"><p>Loading Pulse summaries...</p></div>;
  }

  if (pivotTableState.error && !pivotTableState.data && activeSummary === 'pivot') { // Critical error for pivot
    return <div className="pulse-page-container"><p>Error loading Pivot Table: {pivotTableState.error}. Please try again later.</p></div>;
  }

  // Use samplePivotApiResponse.pivotTable if fetched data is null and no error, 
  // or if there was an error and we decided to fallback (as done in the catch block).
  const currentPivotData: PivotTableData = pivotTableState.data || samplePivotData.pivotTable;
  const allServices = currentPivotData.rows.sort( (a, b) => a < b ? -1 : 1);

  const mainCssUrl = `${process.env.PUBLIC_URL}/static/css/main.css`;
  const isLoadingOverall = (activeSummary === 'pivot' && pivotTableState.isLoading) ||
                           (activeSummary.startsWith('service_') && Object.values(isLoadingAttributes).some(Boolean)) ||
                           (activeSummary.startsWith('attribute_') && groupAttributeSummaryData.isLoading);

  let viewRenderPayload: ViewRenderPayload;

  if (activeSummary === 'pivot' && currentPivotData) {
    viewRenderPayload = {
      type: 'pivot',
      data: currentPivotData,
    };
  } else if (activeSummary.startsWith('service_') && selectedServiceForAttribute) {
    viewRenderPayload = {
      type: 'serviceDetail',
      serviceName: selectedServiceForAttribute,
      keyAttributes: selectedCustomer?.keyAttributes || [],
      serviceAttributesData: serviceAttributesData,
      isLoadingAttributes: isLoadingAttributes,
      attributeErrors: attributeErrors,
      selectedAttributeForServicePivot: selectedAttributeForServicePivot,
      serviceAttributePivotState: serviceAttributePivotState,
    };
  } else if (activeSummary.startsWith('attribute_') && selectedGroupAttribute && groupAttributeSummaryData.data) {
    viewRenderPayload = {
      type: 'groupAttribute',
      servicesGroup: servicesGroupForDisplay, // Already available
      selectedGroupAttribute: selectedGroupAttribute,
      groupAttributeSummaryState: groupAttributeSummaryData,
    };
  } else {
    viewRenderPayload = { type: 'none' };
  }


  const noDataError = () => {
    return (
      <ErrorDisplay
        sectionTitle="Team Selection Error"
        errorMessage={
          <>
            Failed to load available teams.
            <br />
            <strong>Details:</strong> <span  style={{color: "pink"}}>{pivotTableState.error}</span>
            <br />
            <em>Please try again later or contact support if the issue persists.</em>
          </>
        }
        navButtonText="Go to Home"
        navButtonLink="/"
      />
    );
  }

  return (
    <div className="pulse-page-container">
      {pivotTableState.error
      ? <>{noDataError()}</>
      :
      (<>
      <PulseNav 
        activeSummary={activeSummary}
        urlPathToParent={selectedCustomer.servicesGroup.length > 1 ? "/pulse" : "/teams"}
        onSelectSummary={handleSelectSummary} 
        services={allServices}
        keyAttributes={selectedCustomer?.keyAttributes || []}
        servicesGroup={servicesGroupForDisplay}        
        pulseView={pulseView}
        onPulseViewChange={handlePulseViewChange}
        timeSliceWindow={timeSliceWindow}
        currentEndDate={timeSliceWindow.endTime}
        onEndDateChange={handleEndDateChange}  
        minSelectableDate={selectedCustomer?.minDateAllowed || minSelectableDateString}
        maxSelectableDate={selectedCustomer?.maxDateAllowed || maxSelectableDateString}
      />
      <PulseContent 
        activeSummary={activeSummary}
        servicesGroup={servicesGroupForDisplay}
        isLoadingOverall={isLoadingOverall}
        currentStartDate={timeSliceWindow.startTime}
        currentEndDate={timeSliceWindow.endTime}
        viewRenderPayload={viewRenderPayload}
        mainCssUrl={mainCssUrl}
        // showNotification can be passed from here if useNotifier is centralized
      />
      </>)}
    </div>
  );
};
export default PulsePage;